import { Link } from "react-router-dom";
import { ArrowLeft, Building, Settings, Users, Lightbulb, Award, Globe, Shield, Wifi, Car, Utensils, Waves, Dumbbell, Phone, Mail } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { HotelDetailsModal } from "@/components/HotelDetailsModal";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import windsorLogo from "@/assets/windsor-logo.png";
import windsorHistoric from "@/assets/windsor-historic.jpg";
import museLogo from "@/assets/muse-logo.png";
import museGallery from "@/assets/muse-logo-gallery.png";

const Hotels = () => {
  const {
    t
  } = useLanguage();
  const heroRef = useScrollAnimation();
  const operationsRef = useScrollAnimation();
  const conceptRef = useScrollAnimation();
  const differentiationRef = useScrollAnimation();

  // Hotel modal state
  const [selectedHotel, setSelectedHotel] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Hotel data
  const hotelsData = [
    {
      id: "fanaya-hotel-jeddah",
      name: "Fanaya Hotel – Jeddah",
      type: "Lifestyle Hotel",
      location: "Jeddah, Saudi Arabia",
      description: "A lifestyle hotel that embodies Jeddah's new spirit — progressive, creative, and connected. Designed as a canvas where art, design, and hospitality intersect.",
      longDescription: "Fanaya is a lifestyle hotel that embodies Jeddah's new spirit — progressive, creative, and connected. Designed as a canvas where art, design, and hospitality intersect, Fanaya offers more than a stay; it delivers an immersive cultural experience. Every element, from interiors to programming, reflects a dialogue between contemporary aesthetics and the city's rich heritage. Fanaya is envisioned as a gathering place for travelers and locals alike — a social hub where design-forward spaces, curated dining, and cultural touchpoints come together to define the next chapter of hospitality in Jeddah.",
      features: ["52 Rooms & Suites", "Rooftop Pool & Lounge", "Red Sea Views", "Design-led Interiors", "Cultural Activations", "Co-working Spaces"],
      amenities: [
        { icon: Wifi, name: "Free WiFi" },
        { icon: Car, name: "Complimentary Parking" },
        { icon: Utensils, name: "Signature Restaurant & Café" },
        { icon: Waves, name: "Rooftop Pool & Lounge" },
        { icon: Dumbbell, name: "Fitness Studio" },
        { icon: Users, name: "Concierge Services" }
      ],
      rooms: 52,
      rating: 4.8,
      contact: {
        phone: "+20 12 8349 2222",
        email: "info@brassbellhospitality.com",
        website: "www.fanayahotel.com"
      },
      images: [
        "/lovable-uploads/68dcfe67-0519-46eb-a4e0-2deb821ce2b8.png", // Luxury Lobby
        "/lovable-uploads/f1b54e45-4211-4c14-bda3-aaa05168e413.png", // Contemporary Lounge  
        "/lovable-uploads/9959f55d-071d-448b-9312-ff86709bb6bf.png", // Red Sea View Room
        "/lovable-uploads/0975a0f7-e704-4b2d-89da-0848efc25081.png", // Reception Area
        "/lovable-uploads/8c184763-f4eb-4fec-93dd-33fe74793336.png", // Fitness Studio
        "/lovable-uploads/118a43f1-cace-4735-8783-c459ef7ba19d.png", // Suite Living Area
        "/lovable-uploads/bd16f3f9-0f55-4713-9868-e9d6b86d5c9f.png", // Recreation Room
        "/lovable-uploads/e444cdf6-8079-42eb-bb07-eaf178791be9.png"  // Executive Suite
      ],
      priceRange: "Coming Soon",
      openingYear: 2025
    },
    {
      id: "longchamps-hotel",
      name: "Longchamps Hotel",
      type: "Boutique Hotel",
      location: "Zamalek, Cairo, Egypt",
      description: "Longchamps is one of Cairo's most storied boutique hotels, carrying a legacy that began in 1953. Under Brassbell's stewardship, the hotel has been carefully revitalized to preserve its timeless charm while infusing it with contemporary sophistication.",
      longDescription: "Longchamps is one of Cairo's most storied boutique hotels, carrying a legacy that began in 1953. Under Brassbell's stewardship, the hotel has been carefully revitalized to preserve its timeless charm while infusing it with contemporary sophistication. Situated in Zamalek, Longchamps offers intimate boutique hospitality surrounded by Cairo's cultural, lifestyle, and diplomatic hubs. A Zamalek landmark with rich heritage appeal, the hotel features boutique charm with refreshed contemporary interiors and proximity to cultural and lifestyle attractions.",
      features: ["22 Rooms & Suites", "Heritage Since 1953", "Zamalek Location", "Boutique Charm", "Cultural Hub Access", "Contemporary Interiors"],
      amenities: [
        { icon: Wifi, name: "Free WiFi" },
        { icon: Car, name: "Valet Parking" },
        { icon: Utensils, name: "Restaurant & Breakfast Terrace" },
        { icon: Users, name: "Common Areas & Guest Lounge" },
        { icon: Building, name: "Meeting & Gathering Spaces" },
        { icon: Users, name: "Concierge Services" }
      ],
      rooms: 22,
      rating: 4.7,
      contact: {
        phone: "+20 12 8349 2222",
        email: "info@brassbellhospitality.com",
        website: "www.brassbellhospitality.com"
      },
      images: [
        "/lovable-uploads/0e3dd660-1e5d-406e-bb45-c63f231015e2.png",
        "/lovable-uploads/cdb1c906-2ff4-4de4-9a7a-37f980acce05.png",
        "/lovable-uploads/833dc70d-0d68-4d46-8519-4dc9dad6b579.png",
        "/lovable-uploads/c5ed9ef2-566f-4a1a-a36c-7313bb5f12d8.png",
        "/lovable-uploads/8b7be014-4cbb-467b-b6e8-851aa329505c.png",
        "/lovable-uploads/1c3801b1-b75a-4362-b6ad-f85bbfbb68a9.png",
        "/lovable-uploads/5793ca21-10e8-4ea2-96dd-f30fe29c183f.png",
        "/lovable-uploads/b20ac29b-59e6-4f66-b7b9-399d88f7f352.png",
        "/lovable-uploads/bc63e797-0aee-4b6f-b5b2-2319baf83404.png"
      ],
      priceRange: "Reopening 2025",
      openingYear: 1953
    },
    {
      id: "saint-j-hotel",
      name: "Saint J Hotel",
      type: "Heritage Hotel",
      location: "Downtown Cairo, Egypt",
      description: "Saint J Hotel reimagines a historic Downtown Cairo landmark into a refined boutique hospitality destination. Seamlessly blending heritage and modernity, it offers a distinct sense of place where timeless architecture meets contemporary design.",
      longDescription: "Saint J Hotel reimagines a historic Downtown Cairo landmark into a refined boutique hospitality destination. Seamlessly blending heritage and modernity, it offers a distinct sense of place where timeless architecture meets contemporary design. Saint J positions itself at the cultural heart of Cairo, catering to travelers drawn to history, art, and urban energy. Immersed in Cairo's cultural and artistic district, the hotel represents an adaptive reuse of a landmark heritage property with a fusion of old-world charm and modern amenities.",
      features: ["40 Rooms & Suites", "Heritage Property", "Rooftop Terrace", "City Views", "Art Installations", "Co-working Spaces"],
      amenities: [
        { icon: Wifi, name: "24/7 WiFi" },
        { icon: Car, name: "Valet Parking" },
        { icon: Utensils, name: "Lobby & Reception" },
        { icon: Utensils, name: "Cafe and F&B Concept" },
        { icon: Waves, name: "Rooftop Terrace with City Views" },
        { icon: Users, name: "24/7 Concierge" }
      ],
      rooms: 40,
      rating: 4.8,
      contact: {
        phone: "+20 12 8349 2222",
        email: "info@brassbellhospitality.com",
        website: "www.brassbell.net"
      },
      images: [
        "/lovable-uploads/1e042ad8-3a02-4883-975c-dcb7121c2678.png", // Modern Kitchenette
        "/lovable-uploads/91b63e73-31c3-45f5-a43b-a13a6e6ce38f.png", // Boutique Suite Living Area
        "/lovable-uploads/c533211f-79ab-465c-8c55-752bb44f7c6b.png", // Suite with Artistic Lighting
        "/lovable-uploads/250a5596-0286-47e3-9086-5489c6f5310e.png", // Downtown Cairo Views
        "/lovable-uploads/a2425bba-0dd2-4252-a339-7e770539f603.png", // Dining Area by Window
        "/lovable-uploads/6f46cd51-9912-477e-96a0-73a9fc9a747d.png", // Heritage Bathroom
        "/lovable-uploads/432aa14a-27c1-448b-9eff-d249c2137f11.png", // Bedroom with City Views
        "/lovable-uploads/4c273b38-ada8-48bc-b513-f0eac953f3df.png", // Co-working Space
        "/lovable-uploads/74da2458-4ea3-4f31-8554-a05b4e496058.png"  // Executive Suite
      ],
      priceRange: "Coming Soon - 2025",
      openingYear: 2025
    },
    {
      id: "windsor-hotel",
      name: "Windsor Hotel",
      type: "Heritage Hotel",
      location: "Downtown Cairo, Egypt",
      description: "A historic landmark in the heart of Downtown Cairo, soon to join the Brassbell portfolio with a careful restoration blending heritage and modern comfort.",
      longDescription: "The Windsor Hotel is a storied landmark in the heart of Downtown Cairo. Joining the Brassbell portfolio soon, it will be carefully restored and reimagined to blend its rich heritage with contemporary comfort — offering guests an authentic Downtown Cairo experience surrounded by the city's cultural and architectural treasures.",
      features: ["Coming Soon", "Heritage Landmark", "Downtown Cairo", "Restored Interiors"],
      amenities: [
        { icon: Wifi, name: "Free WiFi" },
        { icon: Users, name: "Concierge Services" }
      ],
      rooms: 0,
      rating: 0,
      contact: {
        phone: "+20 12 8349 2222",
        email: "info@brassbellhospitality.com",
        website: "www.brassbellhospitality.com"
      },
      images: [windsorHistoric],
      priceRange: "Coming Soon",
      comingSoon: true,
      openingYear: 2026
    },
    {
      id: "the-muse-hotel",
      name: "The Muse",
      type: "Boutique Hotel",
      location: "New Cairo, Egypt",
      description: "A boutique destination in New Cairo, soon to join the Brassbell portfolio with contemporary design and creative, art-inspired hospitality.",
      longDescription: "The Muse is a boutique destination set in New Cairo. Joining the Brassbell portfolio soon, it will bring contemporary design and creative, art-inspired hospitality to one of Cairo's most vibrant districts — offering a refined stay for travelers drawn to style, culture, and modern comfort.",
      features: ["Coming Soon", "Boutique Living", "New Cairo", "Art-Inspired Design"],
      amenities: [
        { icon: Wifi, name: "Free WiFi" },
        { icon: Users, name: "Concierge Services" }
      ],
      rooms: 0,
      rating: 0,
      contact: {
        phone: "+20 12 8349 2222",
        email: "info@brassbellhospitality.com",
        website: "www.brassbellhospitality.com"
      },
      images: [museGallery],
      priceRange: "Coming Soon",
      comingSoon: true,
      openingYear: 2026
    }
  ];

  const openHotelModal = (hotel: any) => {
    setSelectedHotel(hotel);
    setIsModalOpen(true);
  };

  const closeHotelModal = () => {
    setSelectedHotel(null);
    setIsModalOpen(false);
  };
  
  const operationsServices = [{
    title: "Property Management & Administration",
    description: "Day-to-day hotel management with centralized oversight. Compliance with local regulations, licensing, and brand standards.",
    icon: Building
  }, {
    title: "Rooms Division & Guest Experience",
    description: "Front office, concierge, housekeeping, and guest relations. Training programs ensuring service excellence and brand consistency.",
    icon: Users
  }, {
    title: "Revenue Optimization",
    description: "Proprietary PMS, CRS, and channel manager integrations. Centralized revenue management, pricing strategies, and distribution.",
    icon: Award
  }, {
    title: "Food & Beverage Operations",
    description: "Full-service restaurant and bar management. Curated concept creation and third-party partnerships.",
    icon: Settings
  }];
  
  const conceptServices = [{
    number: "01",
    title: "Market Intelligence & Positioning Advisory",
    description: "Feasibility Studies & Market Sizing – Assessing demand, segmentation, and competitive landscape."
  }, {
    number: "02",
    title: "Concept Development & Guest Experience Design",
    description: "Narrative & Lifestyle DNA Creation – Crafting the story, cultural references, and identity of the property."
  }, {
    number: "03",
    title: "Brand IP Creation & Roll-Out",
    description: "Tailored Brand Development – Creating proprietary hotel and residence brands exclusively for projects."
  }, {
    number: "04",
    title: "Design & Technical Advisory",
    description: "Design Concept Guidelines – Providing direction for interior architecture, FF&E, and OS&E selections."
  }, {
    number: "05",
    title: "Pre-Opening Technical Support",
    description: "Critical Path Planning – Structuring timelines for pre-opening milestones."
  }, {
    number: "06",
    title: "Owner Representation & Development Oversight",
    description: "Stakeholder Alignment – Acting as the owner's technical advisor during design and construction."
  }, {
    number: "07",
    title: "Brand Extension & Intellectual Property Stewardship",
    description: "Portfolio Expansion Strategy – Scaling successful concepts into regional or global footprints."
  }];
  return <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section ref={heroRef.ref} className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden pt-16 sm:pt-20">
        {/* Hotel skyline drawing animation — matches home hero, with Cairo-inspired silhouette */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden opacity-25">
          <svg
            className="w-full h-full"
            viewBox="0 0 1200 400"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
          >
            <path
              d="M0,400 L0,320 L60,320 L60,250 L100,250 L100,320 L140,320 L140,190 L180,190 L180,320 L230,320 L230,150 L245,150 L245,120 L255,120 L255,150 L270,150 L270,320 L340,320 L340,260 C340,205 450,205 450,260 L450,320 L520,320 L520,140 L532,140 L532,320 L600,320 L600,240 L660,240 L660,320 L720,320 L720,170 L780,170 L780,320 L850,320 L850,260 C850,215 940,215 940,260 L940,320 L1010,320 L1010,190 L1070,190 L1070,320 L1140,320 L1140,250 L1200,250 L1200,400"
              stroke="hsl(var(--foreground))"
              strokeWidth="1.5"
              fill="none"
              className="animate-draw-skyline"
            />
            <path
              d="M0,400 L0,320 L60,320 L60,250 L100,250 L100,320 L140,320 L140,190 L180,190 L180,320 L230,320 L230,150 L245,150 L245,120 L255,120 L255,150 L270,150 L270,320 L340,320 L340,260 C340,205 450,205 450,260 L450,320 L520,320 L520,140 L532,140 L532,320 L600,320 L600,240 L660,240 L660,320 L720,320 L720,170 L780,170 L780,320 L850,320 L850,260 C850,215 940,215 940,260 L940,320 L1010,320 L1010,190 L1070,190 L1070,320 L1140,320 L1140,250 L1200,250 L1200,400"
              stroke="hsl(var(--hotel-primary))"
              strokeWidth="2"
              fill="none"
              className="animate-draw-skyline-hotels"
            />
          </svg>
        </div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{
        animationDelay: '1.5s'
      }}></div>
        
        {/* Back button */}
        <Link to="/" className="absolute top-4 left-3 sm:top-6 sm:left-4 md:top-8 md:left-8 flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 z-10 hover-scale group">
          <ArrowLeft className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={18} />
          <span className="hidden sm:inline text-sm">Back to Home</span>
          <span className="sm:hidden text-sm">Back</span>
        </Link>

        <div ref={heroRef.ref as any} className={`container mx-auto px-4 sm:px-6 relative z-10 ${heroRef.isVisible ? 'animate-fade-in-up' : ''}`}>
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-6 sm:space-y-8 md:space-y-12">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-[0.9] tracking-tight text-foreground">Brassbell Hotels</h1>
                <div className="w-12 sm:w-16 md:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-foreground max-w-3xl mx-auto leading-relaxed">
                Brassbell Hotels unites world-class operational expertise with visionary concept creation, delivering a dual capability that is rare in the industry. We bridge discipline and imagination to create hospitality experiences that endure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hotel Management & Operations Section */}
      <section ref={operationsRef.ref} className={`py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 bg-white transition-all duration-1000 ${operationsRef.isVisible ? 'animate-fade-in' : ''}`}>
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-black mb-3 sm:mb-4 md:mb-6 tracking-tight">
              Hotel Management
              <br />
              <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-black/70">& Operations</span>
            </h2>
            <div className="w-12 sm:w-16 md:w-24 h-0.5 bg-black mx-auto" />
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/80 max-w-3xl mx-auto mt-4 sm:mt-6 md:mt-8 leading-relaxed px-4">
              End-to-end hospitality operations designed to deliver consistent service standards, efficient cost structures, and optimized revenues.
            </p>
          </div>
          
          {/* Operations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
            {operationsServices.map((service, index) => <div key={index} className="group bg-white border border-black/10 rounded-2xl p-4 sm:p-6 md:p-8 hover:border-black/30 transition-all duration-500 hover:shadow-lg">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-500 flex-shrink-0">
                    <service.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-black mb-2 md:mb-3 group-hover:text-black/90 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-black/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Additional Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[{
            title: "Sales & Marketing",
            description: "Centralized sales force across corporate, leisure, and MICE segments"
          }, {
            title: "Finance & Asset Protection",
            description: "Budgeting, forecasting, and financial reporting with profitability optimization"
          }, {
            title: "Human Capital & Training",
            description: "Recruitment strategies and brand-specific service culture programs"
          }, {
            title: "Facilities & Technical Services",
            description: "Preventive maintenance and sustainability optimization programs"
          }].map((item, index) => <div key={index} className="group bg-white border border-black/10 rounded-xl p-4 sm:p-6 hover:border-black/30 transition-all duration-300 hover:shadow-md">
                <h4 className="text-sm sm:text-base md:text-lg font-medium text-black mb-2 sm:mb-3 group-hover:text-black/90">
                  {item.title}
                </h4>
                <p className="text-xs sm:text-sm text-black/70 leading-relaxed">
                  {item.description}
                </p>
              </div>)}
          </div>

          {/* White-Label Operations */}
          <div className="mt-12 sm:mt-16 md:mt-20 bg-black/5 rounded-2xl p-6 sm:p-8 md:p-12 text-center">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-black mb-4 sm:mb-6">
                White-Label Operations
              </h3>
              <p className="text-sm sm:text-base md:text-lg text-black/80 leading-relaxed">
                Beyond our proprietary brands, Brassbell Hotels provides white-label operational services for third-party owners and developers. We bring our systems, expertise, and centralized infrastructure, allowing properties to operate with the strength of a branded operator but under the owner's preferred identity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Concept Curation Section */}
      <section ref={conceptRef.ref} className={`py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 bg-white transition-all duration-1000 ${conceptRef.isVisible ? 'animate-fade-in' : ''}`}>
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-black mb-3 sm:mb-4 md:mb-6 tracking-tight">
              Concept Curation
              <br />
              <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-black/70">& Technical Services</span>
            </h2>
            <div className="w-12 sm:w-16 md:w-24 h-0.5 bg-black mx-auto" />
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/80 max-w-4xl mx-auto mt-4 sm:mt-6 md:mt-8 leading-relaxed px-4">
              We are creators of intellectual property and architects of guest experiences, developing unique hospitality ecosystems that extend beyond the walls of a property.
            </p>
          </div>
          
          {/* Concept Services */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            {conceptServices.map((service, index) => <div key={index} className="group bg-white border border-black/10 rounded-2xl p-4 sm:p-6 md:p-8 hover:border-black/30 transition-all duration-500 hover:shadow-lg">
                <div className="flex items-start gap-3 sm:gap-4 md:gap-6">
                  <div className="text-xl sm:text-2xl md:text-4xl font-extralight text-black/20 group-hover:text-black/40 transition-colors duration-500 min-w-[40px] sm:min-w-[50px] md:min-w-[80px] flex-shrink-0">
                    {service.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-black mb-2 sm:mb-3 md:mb-4 group-hover:text-black/90 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base lg:text-lg text-black/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Featured Hotels Section */}
      <section className="py-6 sm:py-8 md:py-10 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 sm:mb-8 md:mb-10">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-foreground mb-3 sm:mb-4 tracking-tight">
              Featured Hotels
            </h2>
            <div className="w-12 sm:w-16 md:w-20 h-0.5 bg-foreground/20 mx-auto" />
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mt-3 sm:mt-4 leading-relaxed">
              Discover our portfolio of exceptional hospitality properties, each crafted to deliver unparalleled guest experiences.
            </p>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {hotelsData.map((hotel, index) => (
                <CarouselItem key={hotel.id} className="pl-2 md:pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                  <div 
                    className="group bg-muted/20 border border-border rounded-2xl overflow-hidden hover:bg-muted/40 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md h-full"
                    onClick={() => openHotelModal(hotel)}
                  >
                    <div className={`aspect-[4/3] flex items-center justify-center overflow-hidden relative ${hotel.id === "windsor-hotel" ? "bg-neutral-900" : "bg-background/30"}`}>
                      <img 
                        src={hotel.id === "fanaya-hotel-jeddah" ? "/lovable-uploads/9c897cb4-875d-4cb6-9b06-58ae542269d5.png" :
                             hotel.id === "longchamps-hotel" ? "/lovable-uploads/d70a20a5-e223-43ff-9716-2b900b0bf33c.png" :
                             hotel.id === "saint-j-hotel" ? "/lovable-uploads/5bc50964-4f9a-4f7a-9395-1d00d14d45b3.png" :
                             hotel.id === "windsor-hotel" ? windsorLogo :
                             hotel.id === "the-muse-hotel" ? museLogo :
                             "/placeholder.svg"} 
                        alt={`${hotel.name} Logo`}
                        className={`w-full h-full object-contain transition-opacity duration-300 ${hotel.id === "windsor-hotel" ? "p-12" : hotel.id === "the-muse-hotel" ? "p-10" : ""}`}
                      />
                      {hotel.id === "fanaya-hotel-jeddah" && (
                        <img 
                          src="/lovable-uploads/35b5835c-42b4-4047-a999-e8e7c250ccea.png"
                          alt={`${hotel.name} Logo Colored`}
                          className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      )}
                      {hotel.id === "longchamps-hotel" && (
                        <img 
                          src="/lovable-uploads/e1e1e43e-1c89-4aa8-a032-546f00f726f3.png"
                          alt={`${hotel.name} Logo Colored`}
                          className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      )}
                      {hotel.id === "saint-j-hotel" && (
                        <img 
                          src="/lovable-uploads/a78a5298-be6c-45e0-998c-6eee13be0796.png"
                          alt={`${hotel.name} Logo Colored`}
                          className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      )}
                      {hotel.comingSoon && (
                        <div className="absolute top-2 right-2 bg-primary/90 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
                          Coming Soon
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        {hotel.type === "Luxury Resort" && <Award className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Boutique Hotel" && <Users className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Lifestyle Hotel" && <Users className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Eco Resort" && <Lightbulb className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Heritage Hotel" && <Award className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Business Hotel" && <Settings className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Aparthotel" && <Building className="w-4 h-4 text-muted-foreground" />}
                        {hotel.type === "Wellness Hotel" && <Waves className="w-4 h-4 text-muted-foreground" />}
                        <span className="text-xs text-muted-foreground">{hotel.type}</span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-light text-foreground mb-2 group-hover:text-foreground/80 transition-colors">
                        {hotel.name}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-3">
                        {hotel.description}
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>• {hotel.rooms ? `${hotel.rooms} ${hotel.type === "Eco Resort" ? "Villas" : "Rooms"}` : "Details Coming Soon"}</span>
                        <span>• {hotel.features[1]}</span>
                        <span>• {hotel.features[2]}</span>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 md:-left-6" />
            <CarouselNext className="-right-4 md:-right-6" />
          </Carousel>
        </div>
      </section>

      {/* Our Differentiation Section */}
      <section ref={differentiationRef.ref} className={`py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 bg-white transition-all duration-1000 ${differentiationRef.isVisible ? 'animate-fade-in' : ''}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-black mb-4 sm:mb-6 md:mb-8 tracking-tight">
              Our Differentiation
            </h2>
            <div className="w-12 sm:w-16 md:w-24 h-0.5 bg-black mx-auto" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center mb-10 sm:mb-12 md:mb-16">
            <div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/90 leading-relaxed mb-4 sm:mb-6">
                What sets Brassbell apart is our fusion of creative brand IP development with rigorous operational consultancy. We bridge the worlds of design thinking, cultural storytelling, and hotel operations.
              </p>
            </div>
            <div className="space-y-4 sm:space-y-6">
              {["Authentic concepts rooted in local context", "Globally relevant brands with long-term scalability", "Commercially robust assets that outperform in competitive markets"].map((item, index) => <div key={index} className="flex items-center gap-3 sm:gap-4">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-black rounded-full flex-shrink-0" />
                  <span className="text-sm sm:text-base md:text-lg text-black/80">{item}</span>
                </div>)}
            </div>
          </div>

          {/* Conclusion */}
          <div className="text-center bg-black/5 rounded-2xl p-6 sm:p-8 md:p-12">
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light text-black leading-relaxed">
              Brassbell Hotels is more than a hotel operator — we are a <span className="text-black font-medium">concept curator</span>, <span className="text-black font-medium">technical advisor</span>, and <span className="text-black font-medium">brand creator</span>. Our unique ability to combine operational discipline with creative innovation makes us a trusted partner for owners, investors, and developers seeking not just to open hotels, but to build enduring hospitality brands.
            </p>
          </div>
        </div>
      </section>

      {/* Hotel Details Modal */}
      <HotelDetailsModal 
        hotel={selectedHotel}
        isOpen={isModalOpen}
        onClose={closeHotelModal}
      />

      <Footer />
      <ScrollToTop />
    </div>;
};
export default Hotels;